import { Component } from '@theme/component';
import { debounce, normalizeString } from '@theme/utilities';

/**
 * @typedef {Object} NcnIngredientsListingRefs
 * @property {HTMLInputElement} searchInput - Search text input
 * @property {HTMLButtonElement} searchClear - Clear search button
 * @property {HTMLElement} filtersContainer - Container for category filter pills
 * @property {HTMLSelectElement} sortSelect - Sort dropdown
 * @property {HTMLElement} grid - Card grid container
 * @property {HTMLElement} resultCount - Visible count display
 */

/**
 * Client-side ingredient filtering, sorting, and search.
 *
 * @extends {Component<NcnIngredientsListingRefs>}
 */
class NcnIngredientsListing extends Component {
  /** @type {string} */
  #activeCategory = 'all';

  /** @type {string} */
  #searchQuery = '';

  /** @type {string} */
  #sortOrder = 'a-z';

  /** @type {HTMLElement[]} */
  #allItems = [];

  /** @type {number} */
  #totalCount = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#allItems = /** @type {HTMLElement[]} */ ([...this.refs.grid.children]);
    this.#totalCount = parseInt(this.refs.resultCount.dataset.total ?? '0', 10) || this.#allItems.length;
  }

  /**
   * Debounced search handler bound to the search input.
   * @param {Event} event
   */
  handleSearch = debounce((event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#searchQuery = normalizeString(input.value.trim());
    this.refs.searchClear.hidden = input.value.length === 0;
    this.#applyFilters();
  }, 200);

  /**
   * Handle keydown on the search input — Escape clears search first,
   * then resets the category filter on a second press.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();

    if (this.refs.searchInput.value.length > 0) {
      this.clearSearch();
      return;
    }

    if (this.#activeCategory !== 'all') {
      this.#resetCategory();
    }
  }

  /**
   * Clear the search input, reset search state, and refocus.
   */
  clearSearch() {
    this.refs.searchInput.value = '';
    this.refs.searchClear.hidden = true;
    this.#searchQuery = '';
    this.#applyFilters();
    this.refs.searchInput.focus();
  }

  /**
   * Handle a category filter pill click.
   * @param {Event} event
   */
  handleFilterClick(event) {
    const pill = /** @type {HTMLButtonElement} */ (/** @type {Element} */ (event.target).closest('button'));
    if (!pill) return;
    const category = pill.dataset.category;
    if (!category) return;

    this.#activeCategory = category;

    for (const btn of this.refs.filtersContainer.querySelectorAll('.ncn-ingredients-listing__pill')) {
      const isActive = btn.dataset.category === category;
      btn.classList.toggle('ncn-ingredients-listing__pill--active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    }

    this.#applyFilters();
  }

  /**
   * Handle sort dropdown change.
   * @param {Event} event
   */
  handleSort(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    this.#sortOrder = select.value;
    this.#applySort();
    this.#applyFilters();
  }

  /**
   * Reset the category filter back to "All".
   */
  #resetCategory() {
    this.#activeCategory = 'all';

    for (const btn of this.refs.filtersContainer.querySelectorAll('.ncn-ingredients-listing__pill')) {
      const isActive = btn.dataset.category === 'all';
      btn.classList.toggle('ncn-ingredients-listing__pill--active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    }

    this.#applyFilters();
  }

  /**
   * Filter visible items based on current search query and active category.
   */
  #applyFilters() {
    let visibleCount = 0;

    for (const item of this.#allItems) {
      const name = item.dataset.ingredientName ?? '';
      const category = item.dataset.ingredientCategory ?? '';

      const matchesCategory = this.#activeCategory === 'all' || category === this.#activeCategory;
      const matchesSearch = this.#searchQuery === '' || normalizeString(name).includes(this.#searchQuery);

      const visible = matchesCategory && matchesSearch;
      item.hidden = !visible;
      if (visible) visibleCount++;
    }

    this.#updateCount(visibleCount);
  }

  /**
   * Sort items array and reorder DOM via a document fragment.
   */
  #applySort() {
    const sorted = [...this.#allItems].sort((a, b) => {
      const nameA = a.dataset.ingredientName ?? '';
      const nameB = b.dataset.ingredientName ?? '';
      const catA = a.dataset.ingredientCategory ?? '';
      const catB = b.dataset.ingredientCategory ?? '';

      if (this.#sortOrder === 'z-a') return nameB.localeCompare(nameA);
      if (this.#sortOrder === 'category') {
        const catCompare = catA.localeCompare(catB);
        return catCompare !== 0 ? catCompare : nameA.localeCompare(nameB);
      }
      return nameA.localeCompare(nameB);
    });

    const fragment = document.createDocumentFragment();
    for (const item of sorted) {
      fragment.appendChild(item);
    }
    this.refs.grid.appendChild(fragment);

    this.#allItems = sorted;
  }

  /**
   * Update the visible count display using the localized template.
   * @param {number} count
   */
  #updateCount(count) {
    const template = this.refs.resultCount.dataset.countTemplate ?? '';
    this.refs.resultCount.textContent = template
      .replace('__COUNT__', String(count))
      .replace('__TOTAL__', String(this.#totalCount));
  }

}

customElements.define('ncn-ingredients-listing', NcnIngredientsListing);
